import { useState, useEffect, useCallback } from 'react';
import { paymentsService, Payment } from '../services/paymentsService';
import { useAuth } from '../context/AuthContext';

export const usePayments = () => {
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { payments: data, error: fetchError } = await paymentsService.getPayments(businessId || undefined);

      if (fetchError) {
        throw fetchError;
      }

      setPayments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    // Only fetch payments when authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      fetchPayments();
    } else if (!authLoading && !isAuthenticated) {
      // Clear data if not authenticated
      setPayments([]);
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, fetchPayments]);

  const refreshPayments = () => {
    fetchPayments();
  };

  return {
    payments,
    loading,
    error,
    refreshPayments,
  };
};

export const usePurchaseOrderPayments = (purchaseOrderId: string | null) => {
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!purchaseOrderId) {
      setLoading(false);
      return;
    }

    // Only fetch payments when authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      const fetchPayments = async () => {
        try {
          setLoading(true);
          setError(null);
          const { payments: data, error: fetchError } = await paymentsService.getPaymentsByPurchaseOrder(purchaseOrderId, businessId || undefined);

          if (fetchError) {
            throw fetchError;
          }

          setPayments(data);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch payments');
        } finally {
          setLoading(false);
        }
      };

      fetchPayments();
    } else if (!authLoading && !isAuthenticated) {
      // Clear data if not authenticated
      setPayments([]);
      setLoading(false);
    }
  }, [purchaseOrderId, authLoading, isAuthenticated, businessId]);

  return {
    payments,
    loading,
    error,
  };
};

export const usePayment = (id: string | null) => {
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [payment, setPayment] = useState<Payment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    // Only fetch payment when authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      const fetchPayment = async () => {
        try {
          setLoading(true);
          setError(null);
          const { payment: data, error: fetchError } = await paymentsService.getPaymentById(id, businessId || undefined);

          if (fetchError) {
            throw fetchError;
          }

          setPayment(data);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch payment');
        } finally {
          setLoading(false);
        }
      };

      fetchPayment();
    } else if (!authLoading && !isAuthenticated) {
      // Clear data if not authenticated
      setPayment(null);
      setLoading(false);
    }
  }, [id, authLoading, isAuthenticated, businessId]);

  return {
    payment,
    loading,
    error,
  };
};